import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCartItems,
  selectCartRestaurantDetails,
  selectCateringItems,
  selectCateringCartPackages,
  selectCateringCartItems,
  selectCateringCartFilters,
  selectIsModifyOrderMode,
  selectModifyOrderPax,
  selectModifyOrderOriginalTotal,
} from "../../redux/features/restaurants/restaurantSelectors";
import {
  setCartItems,
  setCartRestaurantDetails,
  setCartSubTotal,
  setShowProductDetail,
  addVariantItem,
  removeVariantItem,
  setEditingVariantItem,
  setCateringCartItems,
  setCateringCartSubtotal,
  addCateringCartVariantItem,
  removeCateringCartVariantItem,
  addItemToCateringPackage,
  removeItemFromCateringPackage,
  addVariantItemToCateringPackage,
  removeVariantItemFromCateringPackage,
} from "../../redux/features/restaurants/restaurantSlice";
import { updateSubtotal } from "../../utils/menu";
import VariantDropdown from "./variantDropdown";
import MenuItemCard from "./menuItemCard";
import { getItemDetailsAsync } from "../../redux/features/restaurants/restaurantThunks";
import { getCategoryLimitsForPax } from "../../utils/paxCalculations";
import { 
  calculateItemDisplayPriceInPackage, 
  calculateTotalChargeableItemsCost, 
  calculateTotalPackageBasePrice,
  calculateVariantItemPrice,
  getCateringItemPrice
} from "../../utils/catering";

const CateringMenuList = ({ activeCategory, searchQuery, activePackageId = null, disabled = false, isModifyOrderMode = false }) => {
  // Get modify order mode from Redux
  const reduxIsModifyOrderMode = useSelector(selectIsModifyOrderMode);
  const reduxModifyOrderPax = useSelector(selectModifyOrderPax);
  const reduxModifyOrderOriginalTotal = useSelector(selectModifyOrderOriginalTotal);
  
  // Only apply modify order mode if explicitly passed OR Redux state confirms it
  const actualModifyMode = (isModifyOrderMode || reduxIsModifyOrderMode) && 
                          reduxModifyOrderPax !== null;
  
  console.log('CateringMenuList actualModifyMode:', {
    actualModifyMode,
    isModifyOrderMode,
    reduxIsModifyOrderMode,
    reduxModifyOrderPax,
    reduxModifyOrderOriginalTotal
  });
  const cateringItemsList = useSelector(selectCateringItems);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const allCateringCartItems = useSelector(selectCateringCartItems); // Always call the hook
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const filters = useSelector(selectCateringCartFilters);
  const [loadingItemId, setLoadingItemId] = useState(null);
  const dispatch = useDispatch();
  
  // Get items from the active package if available, otherwise from all packages
  const cartItems = activePackageId 
    ? (() => {
        const activePackage = cateringCartPackages.find(pkg => pkg.id === activePackageId);
        return Array.isArray(activePackage?.items) ? activePackage.items : [];
      })()
    : allCateringCartItems; // Use the hook result


  

  
  // Helper function to calculate price based on PAX and add_on_options
  const calculateItemPrice = (item) => {
    const currentPax = filters.pax || 5;
    
    // If item has add_on_options, find the appropriate price based on PAX
    if (item.add_on_options && Array.isArray(item.add_on_options) && item.add_on_options.length > 0) {
      // Check if using new structure with pax_from/pax_to
      if (item.add_on_options[0].pax_from !== undefined && item.add_on_options[0].pax_to !== undefined) {
        // Sort add_on_options by pax_from in descending order to find the highest applicable tier
        const sortedOptions = [...item.add_on_options].sort((a, b) => parseInt(b.pax_from) - parseInt(a.pax_from));
        
        // Find the appropriate add_on_option for current PAX
        const matchingOption = sortedOptions.find(option => 
          currentPax >= parseInt(option.pax_from) && currentPax <= parseInt(option.pax_to)
        );

        if (matchingOption && matchingOption.price) {
          return parseFloat(matchingOption.price);
        }

        // If no exact match, use the lowest tier (fallback to first option)
        if (item.add_on_options.length > 0) {
          return parseFloat(item.add_on_options[0].price);
        }
      } 
      // Check if using old structure with portion_size/additional_price
      else if (item.add_on_options[0].portion_size !== undefined && item.add_on_options[0].additional_price !== undefined) {
        // For old structure, use the first add_on_option's additional_price
        return parseFloat(item.add_on_options[0].additional_price);
      }
    }

    // If no add_on_options or no matching option, return 0 (default free price)
    return 0;
  };

  // Helper function to format price with KWD currency prefix
  const formatPrice = (price) => {
    // If price already includes KWD, return as is
    if (typeof price === "string" && price.startsWith("KWD ")) {
      return price;
    }

    // Convert to number and format with 3 decimal places, then add KWD prefix
    const numericPrice = parseFloat(price);
    return `KWD ${numericPrice.toFixed(3)}`;
  };

  // Get non-variant cart items
  const nonVariantCartItems = cartItems.filter((item) => !item.has_variants);

  // Memoized function to get item quantity from Redux cart
  const getItemQuantity = useCallback(
    (itemId) => {
      const cartItem = cartItems.find(
        (item) => item.id === itemId && !item.has_variants
      );
      return cartItem ? cartItem.quantity : 0;
    },
    [cartItems]
  );

  // Warning modal to be continued - simplified for catering
  const checkExistingCartItems = () => {
    // For catering, we're more flexible with cart items
    return true;
  };

  // Helper function to calculate current total payable
  const calculateCurrentTotal = () => {
    if (!activePackageId) return 0;
    
    const currentPackage = cateringCartPackages.find(pkg => pkg.id === activePackageId);
    if (!currentPackage) return 0;
    
    const currentPax = filters.pax || 20;
    const packageBasePrice = calculateTotalPackageBasePrice([currentPackage], currentPax);
    const chargeableItemsCost = calculateTotalChargeableItemsCost([currentPackage], currentPax);
    
    return packageBasePrice + chargeableItemsCost;
  };

  // In modify mode, validation happens at checkout, not during item add/remove
  // This allows users to freely swap items as long as final total >= original total

  // In modify mode, category limits are flexible to allow swapping items
  // Validation focuses on maintaining minimum total, not category constraints

  // Add item to catering cart (both for new and existing items)
  const addItem = (itemToAdd) => {
    // In modify order mode, allow adding - only enforce minimum total at checkout
    if (cartRestaurantDetails && cartItems.length !== 0) {
      checkExistingCartItems();
    }

    // Look for existing item in cart by ID (avoiding has_variants mismatch)
    const existingItem = cartItems.find(
      (cartItem) => cartItem.id === itemToAdd.id && !cartItem.has_variants
    );

    let updatedItem;
    if (existingItem) {
      // Update existing item (keep all properties intact, just update quantity)
      updatedItem = {
        ...existingItem,
        quantity: existingItem.quantity + 1,
      };
    } else {
      // Create new item (calculate price based on PAX and add_on_options)
      const calculatedPrice = calculateItemPrice(itemToAdd);
      updatedItem = {
        ...itemToAdd,
        quantity: 1,
        price: formatPrice(calculatedPrice),
      };
    }

    // Dispatch updated item to catering cart Redux or nested package
    if (activePackageId) {
      dispatch(addItemToCateringPackage({ packageId: activePackageId, item: updatedItem }));
    } else {
      // For non-package catering items, we need a packageId. Create a default one or skip.
      console.warn('setCateringCartItems now requires packageId. Consider using addItemToCateringPackage instead.');
    }

    // Calculate subtotal from the updated cart items
    const updatedCart = [
      ...cartItems.filter(
        (cartItem) => cartItem.id !== itemToAdd.id || cartItem.has_variants
      ),
      updatedItem,
    ];
    dispatch(setCateringCartSubtotal(updateSubtotal(updatedCart)));
  };

  // Remove item from catering cart
  const removeItem = (itemToRemove) => {
    // Find the exact item in cart (avoiding has_variants mismatch)
    const existingItem = cartItems.find(
      (cartItem) => cartItem.id === itemToRemove.id && !cartItem.has_variants
    );

    if (!existingItem) return;

    // In modify order mode, allow removal - validation happens at checkout
    // Decrement quantity (maintaining all other properties)
    const updatedItem = {
      ...existingItem,
      quantity: existingItem.quantity - 1,
    };

    if (activePackageId) {
      if (updatedItem.quantity <= 0) {
        dispatch(removeItemFromCateringPackage({ packageId: activePackageId, itemId: itemToRemove.id }));
      } else {
        dispatch(addItemToCateringPackage({ packageId: activePackageId, item: updatedItem }));
      }
    } else {
      // For non-package catering items, we need a packageId. Create a default one or skip.
      console.warn('setCateringCartItems now requires packageId. Consider using addItemToCateringPackage instead.');
    }

    // Calculate updated subtotal for the entire catering cart
    let updatedCart;
    if (updatedItem.quantity <= 0) {
      updatedCart = cartItems.filter(
        (cartItem) => cartItem.id !== itemToRemove.id || cartItem.has_variants
      );
    } else {
      updatedCart = cartItems.map((cartItem) =>
        cartItem.id === itemToRemove.id && !cartItem.has_variants
          ? updatedItem
          : cartItem
      );
    }

    dispatch(setCateringCartSubtotal(updateSubtotal(updatedCart)));
  };

  const handleVariantItems = (item, event) => {
    if (event === "add") {
      dispatch(setEditingVariantItem(null));

      // For catering, we don't need to fetch item details as we already have cateringItems
      // Just set the editing state directly
      setLoadingItemId(item.id); // Set loading state briefly for UI feedback
      
      // Find the item details from cateringItems
      const itemDetails = cateringItemsList.find(cateringItem => cateringItem.id === item.id);
      
      if (itemDetails) {
        // Set the editing variant item directly without API call
        dispatch(setEditingVariantItem({
          productInfo: {
            ...itemDetails,
            is_catering: true, // Flag to identify this as a catering item
            catering_item: true,
            packageId: activePackageId // Thread packageId for nested operations
          },
          variantInfo: null // Will be set when specific variant is selected
        }));
        
        // Show the product detail modal for variant selection
        dispatch(setShowProductDetail(true));
      }
      
      setLoadingItemId(null); // Reset loading state immediately
    }
  };

  const handleNonVariantItems = (item, event) => {
    
    dispatch(setShowProductDetail(false));

    // We need to operate on the original item from cateringItemsList
    // Not the displayItem which might have outdated quantity from previous render
    const cateringItem = cateringItemsList.find((cateringItem) => cateringItem.id === item.id);

    if (!cateringItem) return;

    if (event === "add") {
      addItem(cateringItem);
    } else if (event === "remove") {
      removeItem(cateringItem);
    }
  };

  const handleSelectedItem = (item, event) => {
    if (item.has_variants && item.variants?.length !== 0) {
      handleVariantItems(item, event);
    } else {
      handleNonVariantItems(item, event);
    }
  };

  const removeSubItem = (item, variantItem) => {
    // In modify order mode, allow removal - validation happens at checkout
    // Dispatch the variant removal action to catering cart
    if (activePackageId) {
      dispatch(removeVariantItemFromCateringPackage({ packageId: activePackageId, productId: item.id, variantId: variantItem.variantId }));
    } else {
      // For non-package catering items, we need a packageId. Create a default one or skip.
      console.warn('removeCateringCartVariantItem now requires packageId. Consider using removeVariantItemFromCateringPackage instead.');
    }
  };

  const addSubItem = (item, variantItem) => {
    
    // Dispatch the variant add action to catering cart
    if (activePackageId) {
      dispatch(addVariantItemToCateringPackage({ packageId: activePackageId, productId: item.id, variantId: variantItem.variantId }));
    } else {
      // For non-package catering items, we need a packageId. Create a default one or skip.
      console.warn('addCateringCartVariantItem now requires packageId. Consider using addVariantItemToCateringPackage instead.');
    }
  };

  const cardActiveHandler = (itemId, has_variants) => {
    
    if (has_variants) {
      const isVariantInCart = cartItems.some(
        (item) => item.id === itemId && item.has_variants
      );
      return isVariantInCart;
    } else {
      const isItemInCart = cartItems.some(
        (item) => item.id === itemId && !item.has_variants
      );
      return isItemInCart;
    }
  };

  // No need for branch-specific cart restaurant details in catering

  // Return early if no catering items
  if (!cateringItemsList) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-500">No catering items available</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-wrap sm:justify-start justify-center gap-4 mt-4 pb-8">
        {cateringItemsList
          ?.filter(
            (item) =>
              (activeCategory === "All Menu" ||
                item.category_id === activeCategory) &&
              (item.name_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description_en
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()))
          )
          .sort((a, b) => {
            // Only sort when All Menu is selected
            if (activeCategory === "All Menu") {
              return a.category_id - b.category_id;
            }
            return 0;
          })
          .map((item) => {
            // Get current quantity from Redux cart
            const currentQuantity = getItemQuantity(item.id);

            // Calculate the price based on PAX and add_on_options
            const calculatedPrice = calculateItemPrice(item);

            // Format the price to include KWD prefix for display
            const displayItem = {
              ...item,
              price: formatPrice(calculatedPrice),
              quantity: currentQuantity,
            };

            return (
              <div
                key={item.id}
                className={`border-2 p-2 rounded-xl shadow-md flex flex-col gap-4 relative w-[249px] h-36 ${
                  cardActiveHandler(item.id, item.has_variants)
                    ? "border-amber-100"
                    : "border-gray-150"
                } relative transition-all duration-300 ease-in-out`}
                                  style={{
                    marginBottom: (() => {
                      // Find variant items for this product
                      const variantItems = cartItems.find(
                        (variantItem) =>
                          variantItem.id === item.id && variantItem?.has_variants
                      );
                      
                      // If variant items exist, calculate dynamic margin based on number of items
                      if (variantItems) {
                        // Base margin of 50px (approx. the same as mb-20) 
                        // Plus 25px for each additional variant item beyond the first one
                        const itemsCount = variantItems.items?.length || 0;
                        return itemsCount > 0 ? 50 + (itemsCount - 1) * 25 : 0;
                      }
                      
                      return 0;
                    })() + 'px'
                  }}
              >
                <MenuItemCard
                  orderedItems={cartItems}
                  item={displayItem}
                  handleSelectedItem={handleSelectedItem}
                  loadingItemId={loadingItemId}
                  disabled={disabled}
                />
                {cartItems.find(
                  (variantItem) =>
                    variantItem.id === item.id && variantItem.has_variants
                ) && (
                  <VariantDropdown
                    item={cartItems.filter(
                      (variantItem) =>
                        variantItem.id === item.id && variantItem?.has_variants
                    )}
                    removeSubItem={removeSubItem}
                    addSubItem={addSubItem}
                    isCatering={true}
                  />
                )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default CateringMenuList; 